import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BackToTop from './BackToTop';
import SEO from './SEO';
import { useTheme } from '../context/ThemeContext';

// Starburst Icon Component
const StarburstIcon = ({ className = '', color = '#F6CD17' }) => (
  <svg 
    className={className} 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill={color}
  >
    <path d="M12 0L13.5 8.5L22 6L15.5 12L22 18L13.5 15.5L12 24L10.5 15.5L2 18L8.5 12L2 6L10.5 8.5L12 0Z" />
  </svg>
);

// Section Reveal Component
const SectionReveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div 
      ref={ref} 
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      }}
    >
      {children}
    </div>
  );
};

// Arc-Style Interactive Cards Component
const ArcStyleCards = ({ isDarkMode }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const cardData = [
    {
      id: 1,
      number: '01',
      title: 'Strategy First',
      preview: "AI can execute, but only humans can decide what's worth doing.",
      fullContent: "Before any tool touches your brand, we define the 'why' — the strategic foundation that ensures every piece of content, every campaign, and every decision moves you closer to your goals. AI amplifies direction; it doesn't create it.",
      color: '#F6CD17',
    },
    {
      id: 2,
      number: '02', 
      title: 'Context Matters',
      preview: "Your business isn't a template. Neither is our approach.",
      fullContent: "We spend time understanding your market position, competitive landscape, team dynamics, and growth stage. This context shapes how we deploy tools and make recommendations — because a startup and an enterprise need fundamentally different approaches.",
      color: '#35392F',
    },
    {
      id: 3,
      number: '03',
      title: 'Quality Control',
      preview: 'Speed without taste produces noise, not signal.',
      fullContent: 'Every piece of work passes through human judgment. We use AI to move faster through drafts and iterations, but the final call on tone, accuracy, and brand alignment always comes from experienced strategists who understand the nuances.',
      color: '#F6CD17',
    },
    {
      id: 4,
      number: '04',
      title: 'Accountable Partnership',
      preview: 'We stay in the work, not just the deliverables.',
      fullContent: "Unlike agencies that hand off and disappear, we remain engaged throughout execution. We track what's working, adapt quickly when things change, and take responsibility for outcomes — not just outputs.",
      color: '#35392F',
    },
  ];

  return (
    <div className="relative">
      {/* Cards Stack */}
      <div className="space-y-3">
        {cardData.map((card, index) => (
          <div
            key={card.id}
            className="relative"
            onMouseEnter={() => setHoveredCard(card.id)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            {/* Main Card */}
            <div
              className={`relative rounded-2xl overflow-hidden transition-all duration-500 ease-out cursor-pointer ${
                isDarkMode ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'
              }`}
              style={{
                transform: hoveredCard === card.id ? 'scale(1.02)' : 'scale(1)',
                zIndex: hoveredCard === card.id ? 20 : 10 - index,
              }}
            >
              {/* Card Header - Always Visible */}
              <div className="p-5 flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center font-display text-lg font-bold"
                  style={{ 
                    backgroundColor: `${card.color}15`,
                    color: card.color,
                  }}
                >
                  {card.number}
                </div>
                <div className="flex-1">
                  <h4 className={`font-display text-lg pt-1 ${isDarkMode ? 'text-white' : 'text-[#1A1A1A]'}`}>
                    {card.title}
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-black/50'}`}>
                    {card.preview}
                  </p>
                </div>
                {/* Expand Icon */}
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{ 
                    backgroundColor: hoveredCard === card.id ? card.color : 'transparent',
                    transform: hoveredCard === card.id ? 'rotate(45deg)' : 'rotate(0deg)',
                  }}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none" 
                    stroke={hoveredCard === card.id ? (card.color === '#F6CD17' ? '#000' : '#fff') : (isDarkMode ? '#888' : '#666')}
                    strokeWidth="2"
                  >
                    <path d="M8 3v10M3 8h10" />
                  </svg>
                </div>
              </div>

              {/* Expanded Content - Arc Browser Style */}
              <div
                className="overflow-hidden transition-all duration-500 ease-out"
                style={{
                  maxHeight: hoveredCard === card.id ? '200px' : '0px',
                  opacity: hoveredCard === card.id ? 1 : 0,
                }}
              >
                <div className={`px-5 pb-5 pt-2 border-t ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
                  {/* Preview Bar - Like Arc Browser */}
                  <div 
                    className="h-1 rounded-full mb-4 transition-all duration-700"
                    style={{ 
                      backgroundColor: card.color,
                      width: hoveredCard === card.id ? '100%' : '0%',
                    }}
                  />
                  <p className={`leading-relaxed ${isDarkMode ? 'text-white/70' : 'text-black/70'}`}>
                    {card.fullContent}
                  </p>
                </div>
              </div>
            </div>

            {/* Glow Effect on Hover */}
            <div
              className="absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-500"
              style={{
                opacity: hoveredCard === card.id ? 1 : 0,
                boxShadow: `0 0 40px ${card.color}30, 0 0 80px ${card.color}10`,
              }}
            />
          </div>
        ))}
      </div>

      {/* Background Decoration */}
      <div 
        className="absolute -right-20 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full blur-[80px] pointer-events-none"
        style={{ backgroundColor: '#F6CD17', opacity: 0.1 }}
      />
    </div>
  );
};

const WhyUsPage = () => {
  const { isDarkMode } = useTheme();

  const bgClass = isDarkMode ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]';
  const textClass = isDarkMode ? 'text-[#F2F2F2]' : 'text-[#1A1A1A]';
  const subtextClass = isDarkMode ? 'text-[#999999]' : 'text-[#555555]';

  // Feature cards data
  const featureCards = [
    {
      label: 'STRATEGY',
      title: 'Built around you',
      description: 'No templates. No default playbooks. We build around your business, your stage, and how you make decisions — because context changes everything.',
      bg: 'dark',
      accent: '#F6CD17',
      hasImage: true,
      imageStyle: 'blur-motion-1'
    },
    {
      label: 'CREATIVE',
      title: 'Human judgment + modern systems',
      description: 'Intelligent systems help us move faster and work smarter. Human judgment decides what\'s right — so speed never compromises quality or intent.',
      bg: 'light',
      accent: '#F6CD17',
      hasImage: false
    },
    {
      label: 'PARTNERSHIP',
      title: 'Strategic partner, not vendor',
      description: 'We don\'t just execute. We stay in the thinking. Challenging ideas early, sharpening decisions, and staying accountable through execution.',
      bg: 'gradient',
      accent: '#35392F',
      hasImage: true,
      imageStyle: 'blur-motion-2'
    },
    {
      label: 'GROWTH',
      title: 'Clarity over chaos',
      description: 'Growth creates noise. We reduce it. Helping you focus on what matters now, what\'s next, and what can wait — so momentum stays intentional.',
      bg: 'dark',
      accent: '#F6CD17',
      hasImage: false
    },
    {
      label: 'PRESENCE',
      title: 'Presence, not dashboards',
      description: 'You won\'t be handed reports and left to interpret them. We stay close to the work, responsive to change, and responsible for outcomes.',
      bg: 'light',
      accent: '#35392F',
      hasImage: true,
      imageStyle: 'blur-motion-3'
    },
  ];

  const getCardBgClass = (bg) => {
    switch (bg) {
      case 'dark':
        return 'bg-[#0A0A0A] text-white';
      case 'light':
        return isDarkMode ? 'bg-[#1A1A1A] text-white' : 'bg-white text-[#1A1A1A]';
      case 'gradient':
        return 'bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] text-white';
      default:
        return isDarkMode ? 'bg-[#141414] text-white' : 'bg-white text-[#1A1A1A]';
    }
  };

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} transition-colors duration-300`}>
      {/* SEO */}
      <SEO 
        title="Why Choose FLINCH"
        description="Cut through marketing noise with a partner who prioritizes efficiency. Strategy-first, AI-enhanced execution that saves time and drives measurable results."
        canonical="/why-us"
      />
      
      <Header />
      <BackToTop />

      {/* ===== HERO SECTION ===== */}
      <section className="min-h-[80vh] flex items-center pt-24 pb-16 px-6 md:px-8 relative overflow-hidden">
        {/* Dynamic Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-[#F6CD17]/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#35392F]/10 rounded-full blur-[100px]" />
          {/* Decorative Lines */}
          <div className="absolute top-1/3 right-1/4 w-px h-32 bg-gradient-to-b from-transparent via-[#F6CD17]/30 to-transparent" />
          <div className="absolute bottom-1/3 left-1/3 w-px h-48 bg-gradient-to-b from-transparent via-[#35392F]/20 to-transparent" />
        </div>

        <div className="max-w-6xl mx-auto w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <SectionReveal>
                <div className="flex items-center gap-3 mb-6">
                  <StarburstIcon color="#F6CD17" />
                  <span className={`text-xs uppercase tracking-[0.3em] ${subtextClass}`}>
                    WHY FLINCH
                  </span>
                </div>
              </SectionReveal>

              <SectionReveal delay={100}>
                <h1 className={`headline-xl text-[12vw] md:text-[9vw] lg:text-[6vw] ${textClass} mb-8`}>
                  An agency of
                  <br />
                  <span className="text-[#F6CD17]">bold thinkers.</span>
                </h1>
              </SectionReveal>

              <SectionReveal delay={200}>
                <p className={`text-lg md:text-xl ${subtextClass} leading-relaxed max-w-lg mb-8`}>
                  The question isn't whether to use AI. It's who's using it on your behalf — and whether they truly understand your brand.
                </p>
              </SectionReveal>

              <SectionReveal delay={300}>
                <a 
                  href="mailto:contact@weareflinch.com" 
                  className="glass-button-primary inline-flex items-center gap-2"
                >
                  Begin now
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 8H13M13 8L9 4M13 8L9 12" />
                  </svg>
                </a>
              </SectionReveal>
            </div>

            {/* Right Content - Stats/Visual */}
            <div className="hidden lg:block relative">
              <SectionReveal delay={400}>
                <div className="relative">
                  {/* Large decorative text */}
                  <div className="text-[200px] font-display font-bold text-[#F6CD17]/5 leading-none select-none absolute -top-20 -right-10">
                    F.
                  </div>
                  
                  {/* Floating stat cards */}
                  <div className="relative z-10 space-y-4">
                    <div className={`p-6 rounded-2xl ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} backdrop-blur-sm border ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#F6CD17]/10 flex items-center justify-center">
                          <StarburstIcon className="w-6 h-6" color="#F6CD17" />
                        </div>
                        <div>
                          <p className={`text-sm ${subtextClass}`}>Focus on</p>
                          <p className={`font-display text-lg ${textClass}`}>What matters</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`p-6 rounded-2xl ml-12 ${isDarkMode ? 'bg-white/5' : 'bg-black/5'} backdrop-blur-sm border ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#35392F]/10 flex items-center justify-center">
                          <StarburstIcon className="w-6 h-6" color="#35392F" />
                        </div>
                        <div>
                          <p className={`text-sm ${subtextClass}`}>Human-led</p>
                          <p className={`font-display text-lg ${textClass}`}>Strategy first</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT STUDIO SECTION ===== */}
      <SectionReveal>
        <section className={`py-20 md:py-28 px-6 md:px-8 ${isDarkMode ? 'bg-[#0D0D0D]' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <StarburstIcon color="#F6CD17" className="w-8 h-8" />
            </div>
            <h2 className={`font-display text-3xl md:text-4xl lg:text-5xl ${textClass} mb-6`}>
              About Flinch
            </h2>
            <p className={`text-xl md:text-2xl ${textClass} leading-relaxed mb-8`}>
              A marketing partner of <span className="text-[#F6CD17] font-medium">bold creators</span> who believe the best work comes from clear thinking — amplified by modern tools.
            </p>
            <div className={`h-px w-24 mx-auto ${isDarkMode ? 'bg-white/20' : 'bg-black/10'}`} />
          </div>
        </section>
      </SectionReveal>

      {/* ===== FEATURES GRID ===== */}
      <section className="py-20 md:py-28 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <SectionReveal>
            <div className="flex items-center gap-3 mb-12">
              <StarburstIcon color="#F6CD17" />
              <span className={`text-xs uppercase tracking-widest ${subtextClass}`}>
                WHY WORK WITH US
              </span>
            </div>
          </SectionReveal>

          {/* Bento-style Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureCards.map((card, index) => (
              <SectionReveal 
                key={card.title} 
                delay={index * 100}
                className={index === 0 ? 'lg:col-span-2' : index === 2 ? 'lg:row-span-2' : ''}
              >
                <div 
                  className={`relative rounded-3xl overflow-hidden p-8 h-full min-h-[280px] group transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl ${getCardBgClass(card.bg)}`}
                >
                  {/* Background Motion Effect */}
                  {card.hasImage && (
                    <div className="absolute inset-0 overflow-hidden opacity-30 group-hover:opacity-50 transition-opacity duration-500">
                      <div 
                        className={`absolute inset-0 ${
                          card.imageStyle === 'blur-motion-1' 
                            ? 'bg-gradient-to-br from-[#F6CD17]/20 via-transparent to-[#F6CD17]/10' 
                            : card.imageStyle === 'blur-motion-2'
                            ? 'bg-gradient-to-tr from-[#35392F]/20 via-transparent to-[#35392F]/10'
                            : 'bg-gradient-to-bl from-[#F6CD17]/15 via-transparent to-[#35392F]/15'
                        }`}
                      />
                      {/* Motion blur lines */}
                      <div className="absolute inset-0">
                        {[...Array(5)].map((_, i) => (
                          <div 
                            key={i}
                            className="absolute h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"
                            style={{
                              top: `${20 + i * 15}%`,
                              left: '-50%',
                              right: '-50%',
                              transform: `rotate(${-5 + i * 2}deg)`,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card Content */}
                  <div className="relative z-10 h-full flex flex-col">
                    {/* Label */}
                    <div className="flex items-center gap-2 mb-4">
                      <StarburstIcon color={card.accent === '#35392F' ? '#F6CD17' : card.accent} className="w-5 h-5" />
                      <span 
                        className="text-xs font-semibold uppercase tracking-wider"
                        style={{ color: card.accent === '#35392F' && (card.bg === 'dark' || card.bg === 'gradient') ? '#F6CD17' : card.accent }}
                      >
                        {card.label}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-display text-2xl md:text-3xl mb-4 leading-tight">
                      {card.title}
                    </h3>

                    {/* Description */}
                    <p className={`leading-relaxed flex-grow ${card.bg === 'dark' || card.bg === 'gradient' ? 'text-white/80' : isDarkMode ? 'text-white/80' : 'text-[#555555]'}`}>
                      {card.description}
                    </p>

                    {/* Bottom Accent */}
                    <div className="mt-6 pt-4 border-t border-white/10">
                      <div 
                        className="w-12 h-1 rounded-full"
                        style={{ backgroundColor: card.accent === '#35392F' ? '#F6CD17' : card.accent }}
                      />
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== QUOTE SECTION ===== */}
      <SectionReveal>
        <section className="py-20 md:py-28 px-6 md:px-8 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto text-center">
            <StarburstIcon color="#F6CD17" className="w-10 h-10 mx-auto mb-8" />
            <blockquote className="font-display text-3xl md:text-4xl lg:text-5xl text-white leading-[1.5] mb-8 pt-1 pb-2">
              "Tools alone don't build brands.
              <br />
              <span className="text-[#F6CD17]">The way we use them does.</span>"
            </blockquote>
            <p className="text-white/50 text-sm uppercase tracking-wider">
              Our Philosophy
            </p>
          </div>
        </section>
      </SectionReveal>

      {/* ===== THE DIFFERENCE SECTION - Arc-Style Preview ===== */}
      <SectionReveal>
        <section className={`py-20 md:py-28 px-6 md:px-8 ${bgClass}`}>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <StarburstIcon color="#35392F" />
                  <span className={`text-xs uppercase tracking-widest ${subtextClass}`}>
                    THE DIFFERENCE
                  </span>
                </div>
                <h2 className={`font-display text-3xl md:text-4xl ${textClass} mb-6`}>
                  Brands don't want AI.
                </h2>
                <p className={`text-xl md:text-2xl ${textClass} leading-relaxed mb-4`}>
                  They want a partner who <span className="text-[#F6CD17] font-medium">understands them.</span>
                </p>
                <p className={`${subtextClass} leading-relaxed`}>
                  The world is drowning in AI-generated sameness — and brands deserve work that feels personal, performs well, and reflects who they truly are.
                </p>
              </div>

              {/* Arc-Style Interactive Cards */}
              <ArcStyleCards isDarkMode={isDarkMode} />
            </div>
          </div>
        </section>
      </SectionReveal>

      {/* ===== CTA SECTION ===== */}
      <SectionReveal>
        <section className="py-20 md:py-28 px-6 md:px-8 bg-gradient-to-br from-[#0A0A0A] to-[#1A1A1A]">
          <div className="max-w-4xl mx-auto text-center">
            <StarburstIcon color="#F6CD17" className="w-10 h-10 mx-auto mb-8" />
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Ready to work with a partner who gets it?
            </h2>
            <p className="text-white/60 mb-10 max-w-lg mx-auto">
              Let's talk about how Flinch can help your brand move forward with clarity.
            </p>
            <a 
              href="mailto:contact@weareflinch.com" 
              className="glass-button-light inline-flex items-center gap-2"
            >
              Begin now
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" />
              </svg>
            </a>
          </div>
        </section>
      </SectionReveal>

      <Footer />
    </div>
  );
};

export default WhyUsPage;
